import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { useChat, ChatMessage } from "@/hooks/useChat";
import { format } from "date-fns";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { ChatMessageGroup } from "@/components/chat/ChatMessageGroup";
import { ChatInput } from "@/components/chat/ChatInput";
import { Skeleton } from "@/components/ui/skeleton";
import { ConversationList } from "@/components/chat/ConversationList";
import { useIsMobile } from "@/hooks/use-mobile";

const Chat = () => {
  const [input, setInput] = useState("");
  const [session, setSession] = useState<Session | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string>();
  const [showSidebar, setShowSidebar] = useState(false);
  const { messages, isLoading, sendMessage, setMessages } = useChat(session, currentConversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    await sendMessage(userMessage);
  };

  const handleNewChat = async () => {
    setCurrentConversationId(undefined);
    setMessages([]);
    setInput("");
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  const handleSelectConversation = async (conversationId: string) => {
    setCurrentConversationId(conversationId);
    if (isMobile) {
      setShowSidebar(false);
    }
    const { data, error } = await supabase
      .from("Chat")
      .select("*")
      .eq("conversationId", conversationId)
      .order("createdAt", { ascending: true });

    if (error) {
      console.error("Error fetching conversation messages:", error);
      return;
    }

    const chatMessages: ChatMessage[] = (data || []).map(msg => ({
      role: msg.role as 'user' | 'assistant',
      message: msg.message,
      createdAt: msg.createdAt
    }));

    setMessages(chatMessages);
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups: Record<string, typeof messages>, message) => {
    const date = format(new Date(message.createdAt), 'MMMM d, yyyy');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (isLoading && messages.length === 0) {
    return (
      <div className="fixed inset-0 pt-16">
        <div className="h-full mx-auto p-4 flex">
          {(!isMobile || showSidebar) && (
            <div className={`${isMobile ? 'absolute inset-0 z-50 bg-background' : ''}`}>
              <ConversationList
                onSelectConversation={handleSelectConversation}
                onNewChat={handleNewChat}
                currentConversationId={currentConversationId}
              />
            </div>
          )}
          <div className="flex-1 bg-background rounded-lg border shadow-sm h-full flex flex-col">
            <ChatHeader 
              onNewChat={handleNewChat} 
              onToggleSidebar={() => setShowSidebar(!showSidebar)}
              isMobile={isMobile}
              showSidebar={showSidebar}
            />
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-16 w-3/4" />
                </div>
              ))}
            </div>
            <ChatInput 
              input=""
              isLoading={true}
              onInputChange={() => {}}
              onSubmit={() => {}}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 pt-16">
      <div className="h-full mx-auto p-4 flex">
        {(!isMobile || showSidebar) && (
          <div className={`${isMobile ? 'absolute inset-0 z-50 bg-background' : ''}`}>
            <ConversationList
              onSelectConversation={handleSelectConversation}
              onNewChat={handleNewChat}
              currentConversationId={currentConversationId}
            />
          </div>
        )}
        <div className="flex-1 bg-background rounded-lg border shadow-sm h-full flex flex-col">
          <ChatHeader 
            onNewChat={handleNewChat}
            onToggleSidebar={() => setShowSidebar(!showSidebar)}
            isMobile={isMobile}
            showSidebar={showSidebar}
          />
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {Object.entries(groupedMessages).map(([date, dateMessages]) => (
              <ChatMessageGroup 
                key={date} 
                date={date} 
                messages={dateMessages} 
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
          <ChatInput 
            input={input}
            isLoading={isLoading}
            onInputChange={setInput}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default Chat;